import { FC, useCallback, useEffect, useState } from 'react';
import { WarningOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Input, Modal, Typography } from 'antd';
import styled from 'styled-components';
import { useProcessingState, ProcessingType } from '@/entities/proc';
import { ProcProcessingState } from '@/entities/proc/proc-generation.types';
import { useAuth } from '@/features/auth';
import { useNotificationContext } from '@/features/notifications';
import {
  useStartProcCreation,
  useGetWorkspaces,
  AttachedFileDto,
} from '@/shared/api';
import { useRouterPrivateContext } from '@/shared/router';
import { themeConfig, useThemeContext } from '@/shared/theme';
import { Col, Row } from '@/shared/ui';
import { KnowledgeBase } from '@/widgets/knowledge-base/KnowledgeBase';

type CreateProcUsingAiModalProps = {
  open: boolean;
  handleCloseModal: (open: boolean) => void;
};

const { Title } = Typography;

export const CreateProcUsingAiModal: FC<CreateProcUsingAiModalProps> = ({
  open,
  handleCloseModal,
}) => {
  const { accessToken } = useAuth();
  const { theme } = useThemeContext();
  const { showNotification } = useNotificationContext();
  const { selectedWorkspace } = useRouterPrivateContext();
  const queryClient = useQueryClient();
  const [isCreatingProc, setIsCreatingProc] = useState(false);

  useProcessingState<ProcProcessingState>(ProcessingType.PROC_PROCESSING, {
    onStart: () => {
      setIsCreatingProc(true);
      handleCloseModal(false);
      queryClient.invalidateQueries(['WorkspaceProcsGetPaginated']);
      queryClient.invalidateQueries(['dashboardProcsGetPaginated']);
      queryClient.invalidateQueries(['myProcs']);
    },
    onError: (error) => {
      setIsCreatingProc(false);
      handleCloseModal(false);
      showNotification({
        type: 'error',
        message: 'Failed to start proc creation',
        description: error,
      });
    },
  });

  const [prompt, setPrompt] = useState<string | null>(null);
  const [files, setFiles] = useState<AttachedFileDto[]>([]);

  const { data: workspacesData } = useGetWorkspaces(
    {},
    { select: (data) => data?.data },
  );

  const { mutate: createProc, isLoading: createProcLoading } =
    useStartProcCreation();

  const handleProcCreate = async () => {
    if (!prompt) {
      return;
    }

    const workspaceId = selectedWorkspace?.id || workspacesData?.[0]?.id;

    if (!workspaceId) {
      showNotification({
        type: 'error',
        message: 'Create proc error',
        description: 'No workspace available to create proc in',
      });
      return;
    }

    setIsCreatingProc(true);

    createProc(
      {
        body: {
          workspaceId,
          message: prompt,
          files: files,
        },
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
      },
      {
        onSuccess: () => {
          // The modal will be closed by the socket event handler in useProcessingState
          // when it receives the START event with the proc ID
        },
        onError: () => {
          setIsCreatingProc(false);
          showNotification({
            type: 'error',
            message: 'Failed to start proc creation',
            description:
              'There was an error initiating the proc creation process',
          });
          handleCloseModal(false);
        },
      },
    );
  };

  const closeKnowledgeBase = useCallback(
    () => handleCloseModal(false),
    [handleCloseModal],
  );

  useEffect(() => {
    setPrompt(null);
    setFiles([]);
    setIsCreatingProc(false);
  }, [open]);

  return (
    <>
      <Modal
        data-testid="create-proc-using-ai-modal"
        title={
          <Col
            alignItems="flex-start"
            style={{ margin: '10px 0', width: '100%' }}
          >
            <Title level={2} style={{ lineHeight: 1, marginTop: 15 }}>
              Create new proc using AI
            </Title>
          </Col>
        }
        destroyOnClose={true}
        width={1300}
        open={open}
        footer={null}
        style={{ maxWidth: '90%' }}
        onCancel={() => !isCreatingProc && handleCloseModal(false)}
        maskClosable={!isCreatingProc}
      >
        <Wrapper>
          <>
            <div style={{ marginTop: 30 }}>
              <AIPromptLabelWrapper>
                <div style={{ marginTop: 30 }}>
                  <label style={{ color: themeConfig[theme].colors.text }}>
                    Describe proc you want to create
                  </label>
                  <Input.TextArea
                    data-testid="ai-prompt-input"
                    disabled={createProcLoading}
                    rows={5}
                    maxLength={400}
                    showCount={true}
                    style={{ marginTop: 10 }}
                    value={prompt || ''}
                    onChange={(e) => setPrompt(e.target.value)}
                  />
                </div>
              </AIPromptLabelWrapper>
            </div>
            <WarningWrapper $show={files.length === 0}>
              <WarningInfo>
                <WarningOutlined style={{ marginRight: 8, fontSize: 14 }} />
                If no files are selected, the AI will use its general knowledge.
                For more accurate responses, we recommend selecting relevant
                files.
              </WarningInfo>
            </WarningWrapper>
            <KnowledgeBase
              isReadOnly
              onCancel={closeKnowledgeBase}
              onFilesSelect={setFiles}
            />
            <Actions>
              <ButtonsWrapper>
                <Button
                  onClick={() => {
                    setFiles([]);
                    handleCloseModal(false);
                  }}
                  style={{ width: '40%', height: 40, marginTop: 30 }}
                  disabled={isCreatingProc}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  loading={createProcLoading || isCreatingProc}
                  disabled={!prompt || createProcLoading || isCreatingProc}
                  onClick={handleProcCreate}
                  style={{
                    width: '60%',
                    height: 40,
                    marginTop: 30,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  Create a proc
                </Button>
              </ButtonsWrapper>
            </Actions>
          </>
        </Wrapper>
      </Modal>
    </>
  );
};

const Wrapper = styled.div`
  padding-bottom: 10px;
  width: 100%;
`;

const Actions = styled(Row)`
  justify-content: flex-end;
  margin-top: 30px;
  width: 100%;
`;

const ButtonsWrapper = styled(Row)`
  justify-content: space-between;
  width: 100%;
  max-width: 450px;
`;

const AIPromptLabelWrapper = styled.div`
  width: 100%;

  .ant-input-data-count {
    font-size: 11px;
  }
`;

const WarningWrapper = styled.div<{ $show: boolean }>`
  max-height: ${(props) => (props.$show ? '200px' : '0')};
  opacity: ${(props) => (props.$show ? '1' : '0')};
  transition: all 0.3s ease-in-out;
  overflow: hidden;
`;

const WarningInfo = styled.div`
  background-color: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 6px;
  padding: 12px;
  font-size: 12px;
  margin-top: 16px;
  color: #ad6800;
  display: flex;
  align-items: center;
`;
